/**
 * Logger utility that automatically removes logs in production
 * Only logs in development or dev deployment environment
 *
 * Environment detection:
 * - NODE_ENV === "development" → true (local dev with `npm run dev`)
 * - NEXT_PUBLIC_ENVIRONMENT === "development" → true (dev deployment)
 * - Both false → false (production deployment)
 */

import { useConfigStore } from "@/store/configStore";

class Logger {
  /**
   * Dynamically check if we're in development mode
   * Checks both NODE_ENV (local dev) and store environment (deployment env)
   */
  //  * changed it from a constructor check to a dynamic getter so it reads the current store state each time.
  //  * Why this is better:
  //  * The logger singleton may be created before the config store is populated
  //  * A getter checks the store on each log call, so it always uses the current value
  //  * Once the config loads, the logger will use the correct environment
  private get isDevelopment(): boolean {
    return (
      process.env.NODE_ENV === "development" ||
      useConfigStore.getState().environment === "development"
    );
  }

  /**
   * Debug level logging - only in development
   */
  debug(...args: unknown[]): void {
    if (this.isDevelopment) {
      console.log("[DEBUG]", ...args);
    }
  }

  /**
   * Info level logging - only in development
   */
  info(...args: unknown[]): void {
    if (this.isDevelopment) {
      console.info("[INFO]", ...args);
    }
  }

  /**
   * Warning level logging - only in development
   */
  warn(...args: unknown[]): void {
    if (this.isDevelopment) {
      console.warn("[WARN]", ...args);
    }
  }

  /**
   * Error level logging - always logs (even in production)
   * Use this for critical errors that need to be tracked
   */
  error(...args: unknown[]): void {
    console.error("[ERROR]", ...args);
  }

  /**
   * Log level logging - alias for info
   */
  log(...args: unknown[]): void {
    this.info(...args);
  }
}

// Export singleton instance
export const logger = new Logger();

// Export class for testing purposes
export { Logger };
